import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from 'Utils/formatters';
import { cn } from 'Utils/cn';
import useLocale from 'Hooks/useLocale';
import { DRAWER_STACK, INVOICE_TYPES, INVOICES_CREATION_STATUSES } from 'Utils/constants';
import InvoiceTitle from 'Components/Atoms/InvoiceTitle/InvoiceTitle';
import IconInvoiceType from 'Components/Atoms/IconInvoiceType/IconInvoiceType';
import Badge from 'Components/Atoms/Badge/Badge';
import IconButton from 'Components/Atoms/IconButton/IconButton';
import OutlineCloseSmall from 'Components/Atoms/OutlineIcons/OutlineCloseSmall';
import useDrawerStack from 'Hooks/useDrawerStack';
import Skeleton from 'Components/Atoms/Skeleton/Skeleton';
import Button from 'Components/Atoms/Button/Button';
import { attachInvoiceFile } from 'Services/invoices';
import { useQueryClient } from '@tanstack/react-query';

const pendingStatuses = [
    INVOICES_CREATION_STATUSES.PENDING_SCAN,
    INVOICES_CREATION_STATUSES.PENDING_UPLOAD,
    INVOICES_CREATION_STATUSES.PENDING_SCAN_RETRY,
];

function formatDate(date, locale) {
    if (!date) return '-';

    return date.toLocaleDateString(locale, { month: '2-digit', day: '2-digit', year: 'numeric' });
}

export default function CardInvoice(props) {
    const {
        type,
        subtype,
        contactName,
        serieFormatted,
        isReconciled,
        datePayment,
        amount,
        currency,
        className,
        onDelete,
        invoiceId,
        status,
        showValidate,
    } = props;

    const queryClient = useQueryClient();

    const locale = useLocale();
    const { t } = useTranslation('invoices');
    const isIncome = type === INVOICE_TYPES.SALE;

    const displayName = contactName ?? <span className="opacity-50">{t('no-contact-name')}</span>;
    const displayNumber = serieFormatted || '-';

    const { open } = useDrawerStack();

    const handleClick = () => {
        open(DRAWER_STACK.INVOICE_QUICK_EDITOR, {
            invoiceId,
            title: <InvoiceTitle invoice={{ subtype }} />,
        });
    };

    const handleUpload = async (id, file) => {
        await attachInvoiceFile(id, file);
        await queryClient.invalidateQueries({ queryKey: ['invoices', id] });
    };

    const handleClickValidate = () => {
        open(DRAWER_STACK.INVOICE_VALIDATION, {
            title: t('validate-invoice'),
            invoiceId,
            onUpload: (file) => handleUpload(invoiceId, file),
            onSubmit: async () => {
                await queryClient.invalidateQueries({ queryKey: ['invoices'] });
            },
        });
    };

    return (
        <div
            className={cn(
                'relative group grid grid-cols-[32px_1fr_auto] sm:grid-cols-[32px_2fr_1fr_1fr] items-center gap-3 p-3 bg-white ring ring-gray-200 text-dark-blue text-left rounded-lg',
                className,
            )}
        >
            <div>
                <IconInvoiceType invoice={{ type }} />
            </div>
            {pendingStatuses.includes(status) ? (
                <>
                    <div className="overflow-hidden">
                        <div className="text-sm inline-block font-semibold">{t('scanning')}</div>
                        <div>
                            <Skeleton className="w-20" height={12} />
                        </div>
                    </div>

                    <div className="hidden sm:block">
                        <div className="uppercase font-semibold text-2xs tracking-widest text-gray-500">
                            {t('payment-date')}
                        </div>
                        <div>
                            <Skeleton className="w-12" height={12} />
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div className="overflow-hidden">
                        <div className="flex">
                            <div
                                className="text-sm inline-block font-semibold truncate cursor-pointer"
                                tabIndex={0}
                                onClick={handleClick}
                                title={contactName || t('no-contact-name')}
                            >
                                {displayName}
                            </div>
                        </div>
                        <div className="truncate text-xs text-gray-500 flex gap-2 items-center">
                            <span>{displayNumber}</span>
                            {isReconciled && <Badge className="text-2xs py-0.5">{t('reconciled')}</Badge>}
                        </div>
                    </div>

                    <div className="hidden sm:block">
                        {!showValidate && (
                            <>
                                <div className="uppercase font-semibold text-2xs tracking-widest text-gray-500">
                                    {t('payment-date')}
                                </div>
                                <div className="text-xs">{formatDate(datePayment, locale)}</div>
                            </>
                        )}
                    </div>
                </>
            )}

            <div className="text-right flex justify-end items-center gap-1">
                <div className={cn('text-sm font-medium text-right font-mono pr-1', isIncome && 'text-income')}>
                    {amount == null ? '-' : formatCurrency(amount, { currency, locale })}
                </div>

                {showValidate && (
                    <Button size="small" className="h-8" variant="primary" onClick={handleClickValidate}>
                        {t('validate')}
                    </Button>
                )}
                {onDelete && (
                    <IconButton
                        size="small"
                        icon={<OutlineCloseSmall />}
                        tooltip={t('dissociate-invoice')}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete();
                        }}
                    />
                )}
            </div>
        </div>
    );
}

CardInvoice.propTypes = {
    type: PropTypes.string,
    contactName: PropTypes.string,
    serieFormatted: PropTypes.string,
    isReconciled: PropTypes.bool,
    datePayment: PropTypes.instanceOf(Date),
    amount: PropTypes.number,
    currency: PropTypes.string,
    className: PropTypes.string,
    onDelete: PropTypes.func,
    invoiceId: PropTypes.string.isRequired,
    subtype: PropTypes.string,
    status: PropTypes.string,
    showValidate: PropTypes.bool,
};
